import React, { useState } from 'react';
import Icon from './Icon';
import { Listing, PetPolicy, LeaseTerm, listingTypeLabel } from '../api/listings';

const API_ORIGIN = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'
).replace(/\/api\/?$/, '');

const resolvePhoto = (u: string): string =>
  /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`;

const petPolicyLabel: Record<PetPolicy, string> = {
  allowed: 'Pet Friendly',
  'not-allowed': 'No Pets',
  'cats-only': 'Cats Only',
  'dogs-only': 'Dogs Only',
};

const leaseTermLabel: Record<LeaseTerm, string> = {
  'month-to-month': 'Month-to-month',
  '6-months': '6 months',
  '1-year': '1 year',
  '2-years': '2 years',
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPrice = (price: Listing['price'], type: Listing['type']): string => {
  const n = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(n)) return '—';
  const formatted = '$' + n.toLocaleString();
  return type === 'Rental' ? `${formatted}/mo` : formatted;
};

const initials = (name?: string): string =>
  (name || '?')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const Fact: React.FC<{ icon: string; value: React.ReactNode; label: string }> = ({
  icon,
  value,
  label,
}) => (
  <div
    className="rounded-xl flex flex-col items-center justify-center py-2.5 px-2 min-w-0"
    style={{ background: '#fff', border: '1px solid var(--g200)' }}
  >
    <Icon name={icon} size={18} color="var(--brand)" />
    <div className="text-[15px] font-extrabold text-ink mt-1 leading-none truncate max-w-full">
      {value}
    </div>
    <div className="text-[11px] text-g500 mt-0.5 whitespace-nowrap">{label}</div>
  </div>
);

interface Props {
  listing: Listing;
}

const ListingDetailInline: React.FC<Props> = ({ listing }) => {
  const photos = (listing.photos && listing.photos.length
    ? listing.photos
    : listing.coverPhoto
    ? [listing.coverPhoto]
    : []
  ).map(resolvePhoto);
  const [idx, setIdx] = useState(0);
  const hero = photos[idx];

  const amenities = Array.isArray(listing.amenities) ? listing.amenities : [];
  const chips: string[] = [...amenities];
  if (listing.petPolicy) chips.push(petPolicyLabel[listing.petPolicy]);

  return (
    <div
      className="rounded-[14px] p-4 flex gap-5 flex-wrap"
      style={{ background: '#fff', border: '1px solid var(--g200)' }}
    >
      {/* Gallery */}
      <div className="flex-shrink-0" style={{ width: 240 }}>
        <div
          className="rounded-xl overflow-hidden relative"
          style={{ width: 240, height: 170, background: 'var(--g100)' }}
        >
          {hero ? (
            <img src={hero} alt={listing.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="building-2" size={36} color="var(--g400)" />
            </div>
          )}
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIdx((i) => (i - 1 + photos.length) % photos.length);
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.9)' }}
              >
                <Icon name="chevron-right" size={14} className="rotate-180" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIdx((i) => (i + 1) % photos.length);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.9)' }}
              >
                <Icon name="chevron-right" size={14} />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(0,0,0,0.55)', color: '#fff' }}>
                {idx + 1}/{photos.length}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="flex-1 min-w-[280px]">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[22px] font-extrabold text-ink leading-none">
            {formatPrice(listing.price, listing.type)}
          </span>
          <span
            className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
            style={{ background: 'var(--brand-light)', color: 'var(--brand)' }}
          >
            {listing.type === 'Rental' ? 'For Rent' : listingTypeLabel(listing.type)}
          </span>
          <span
            className="px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize"
            style={{
              background: listing.status === 'active' ? 'var(--green-bg)' : 'var(--g100)',
              color: listing.status === 'active' ? 'var(--green)' : 'var(--g500)',
            }}
          >
            {listing.status}
          </span>
        </div>
        <div className="text-[14px] font-semibold text-ink mt-2">{listing.title}</div>
        <div className="text-[12px] text-g500 mt-0.5 flex items-center gap-1.5">
          <Icon name="map-pin" size={13} color="var(--g400)" /> {listing.address}
        </div>

        {/* Facts */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
          <Fact icon="bed" value={listing.beds} label="Beds" />
          <Fact icon="bath" value={listing.baths} label="Baths" />
          <Fact
            icon="maximize"
            value={listing.sqft ? listing.sqft.toLocaleString() : '—'}
            label="Sqft"
          />
          <Fact
            icon="calendar"
            value={listing.leaseTerm ? leaseTermLabel[listing.leaseTerm] : '—'}
            label="Lease"
          />
          <Fact icon="calendar" value={formatDate(listing.availableFrom)} label="Available" />
          <Fact icon="building-2" value={listingTypeLabel(listing.type)} label="Type" />
        </div>

        {/* Description */}
        <div className="mt-3">
          <div className="text-[12px] font-bold text-g500 uppercase tracking-wider mb-1">
            About This Property
          </div>
          <p className="text-[13px] text-g700 leading-relaxed whitespace-pre-line">
            {listing.description || 'No description provided.'}
          </p>
        </div>

        {/* Amenities */}
        {chips.length > 0 && (
          <div className="mt-3">
            <div className="text-[12px] font-bold text-g500 uppercase tracking-wider mb-1.5">
              Amenities &amp; Policies
            </div>
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium"
                  style={{ background: 'var(--g50)', color: 'var(--g700)' }}
                >
                  <Icon name="check-circle" size={12} color="var(--brand)" /> {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Agent */}
        <div className="mt-3 flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold flex-shrink-0"
            style={{ background: 'var(--brand-light)', color: 'var(--brand)' }}
          >
            {initials(listing.agent?.name)}
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-ink truncate">
              {listing.agent?.name || 'Unknown merchant'}
              <span className="text-g400 font-normal"> · Property Merchant</span>
            </div>
            {listing.agent?.email && (
              <div className="text-[12px] text-g500 truncate">{listing.agent.email}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetailInline;
